import type { ReactNode } from "react";

import { ChevronLeft, Edit01 } from "@untitledui/icons";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badges/badges";
import { Button } from "@/components/ui/buttons/button";

interface LocationManagementHeaderProps {
	actions?: ReactNode;
	backHref?: string;
	backLabel?: string;
	badges?: string[];
	description?: string;
	/** Secondary line under the description, smaller than the address. */
	hint?: string;
	onEdit?: () => void;
	title: string;
}

export const LocationManagementHeader = ({
	actions,
	backHref,
	backLabel = "All locations",
	badges,
	description,
	hint,
	onEdit,
	title,
}: LocationManagementHeaderProps) => (
	<div className="relative flex shrink-0 flex-col items-start gap-4 border-b border-secondary bg-primary px-4 py-5 md:flex-row md:items-start md:px-6">
		<div className="flex min-w-0 flex-1 flex-col gap-0.5">
			{backHref ? (
				<Link
					className="mb-1 flex w-fit items-center gap-1 text-sm text-tertiary underline-offset-2 hover:text-secondary hover:underline"
					to={backHref}
				>
					<ChevronLeft className="size-4" /> {backLabel}
				</Link>
			) : null}
			<div className="flex flex-wrap items-center gap-2">
				<h2 className="text-lg font-semibold text-primary">{title}</h2>
				{badges?.map((item) => (
					<Badge color="brand" key={item} size="sm">
						{item}
					</Badge>
				))}
			</div>
			{description ? (
				<div className="flex items-center gap-2">
					<p className="text-sm text-tertiary">{description}</p>
					{onEdit ? (
						<Button
							aria-label="Edit location"
							color="link-color"
							iconLeading={Edit01}
							noBorders
							noTextPadding
							onClick={onEdit}
							size="xs"
						/>
					) : null}
				</div>
			) : null}
			{hint ? <p className="text-xs text-quaternary">{hint}</p> : null}
		</div>
		{actions ? <div className="shrink-0 self-start">{actions}</div> : null}
	</div>
);
